import type { ClassValue } from 'clsx';

import type { Occupancy as BookingOccupancy } from '@elara/domain/models/schemas/booking-schemas';
import type { Occupancy as RateOccupancy } from '@elara/domain/models/schemas/room-schemas';

import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

import { isSignal } from '@preact-signals/utils';
import { clsx } from 'clsx';
import { isPlainObject, mapValues } from 'lodash-es';
import { twMerge } from 'tailwind-merge';

// Pure cross-client rules now live in @elara/domain (web + native import identical code);
// re-exported here so existing web call sites are unchanged.
export { calculateTotalsByCurrency } from '@elara/domain/pricing';
export { metadataForIdentity } from '@elara/domain/item-identity';

/**
 * A utility function to conditionally join CSS class names.
 * It merges Tailwind CSS classes without style conflicts.
 *
 * @param inputs - A list of class names or conditional class objects.
 * @returns The final, merged class name string.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a price to a currency string
 * @param price - The price to format
 * @param currency - The currency to format the price in
 * @returns The formatted price string
 */
export function formatPrice(price: number, currency: string): string {
  const isWholeNumber = price % 1 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: isWholeNumber ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(price);
}

/**
 * Format an amount given in a currency's minor units (Stripe's representation) as a display price.
 *
 * The exponent is read from `Intl` rather than assumed to be 2, because it is not universal — JPY
 * and KRW have none, so dividing by 100 would quote a hundredth of the real price. Asking `Intl`
 * for the currency's own fraction digits keeps that correct without a lookup table to maintain.
 *
 * @example
 * formatMinorUnits(249_900, 'usd'); // '$2,499.00'
 * formatMinorUnits(250_000, 'jpy'); // '¥250,000'
 */
export function formatMinorUnits(amount: number, currency: string): string {
  const format = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() });
  // `?? 2` is only there to satisfy the optional type: `resolvedOptions()` always reports fraction
  // digits for `style: 'currency'`, and 2 is the right fallback for the plan's own currency anyway.
  const exponent = format.resolvedOptions().maximumFractionDigits ?? 2;
  return format.format(amount / 10 ** exponent);
}

/**
 * Convert a country code to its corresponding emoji flag
 * @param countryCode - The ISO 3166-1 alpha-2 country code (e.g., 'US', 'GB', 'FR')
 * @returns The emoji flag for the country, or '🏳️' if invalid
 */
export function getEmojiFlag(countryCode: string | undefined): string {
  if (!countryCode || countryCode.length !== 2) {
    return '🏳️';
  }

  // eslint-disable-next-line unicorn/prefer-spread -- Array.from needed for TypeScript compatibility
  const codePoints = Array.from(countryCode.toUpperCase()).map((char) => 127_397 + char.codePointAt(0)!);

  return String.fromCodePoint(...codePoints);
}

export function firstLine(message: string): string {
  return message.split('\n')[0] ?? message;
}

export function deepValueMap<T>(
  value: T,
  mapper: (value: unknown) => unknown | undefined,
  args: { compact?: boolean } = {},
  visited = new Set<unknown>(),
): T {
  if (visited.has(value)) return value;
  else visited.add(value);
  if (typeof value !== 'object' || value === null) return mapper(value) as T;

  if (!isPlainObject(value)) return mapper(value) as T;
  const res = mapValues(value, (rawValue) => {
    const value = isSignal(rawValue) ? rawValue.peek() : rawValue;
    return deepValueMap<unknown>(value, mapper, args, visited);
  }) as T;
  if (args.compact) {
    for (const key in res) {
      if (res[key] === undefined) delete res[key];
    }
  }
  return res;
}

/**
 * A utility for partial application of a function (currying).
 *
 * @param fn - The function to apply arguments to.
 * @param front - The arguments to apply to the front of the function's argument list.
 * @returns A new function that accepts the remaining arguments.
 * @deprecated This function is not currently used in the codebase and could be removed.
 */
export function apply<T extends unknown[], U extends unknown[], R>(fn: (...args: [...T, ...U]) => R, ...front: T) {
  return (...tailArgs: U) => fn(...front, ...tailArgs);
}

export function pluralize(count: number, label: string): string {
  return new Intl.PluralRules('en-US').select(count) === 'one' ? label : `${label}s`;
}

/**
 * Parse a document id from a reference that may be a path (e.g. "hotels/abc123") or a plain id ("abc123").
 * @param value - Reference string or null/undefined
 * @returns The document id, or undefined if value is falsy
 */
export function parseDocumentId(value: string | null | undefined): string | undefined {
  if (value == null || value === '') return undefined;
  return value.includes('/') ? (value.split('/').findLast(Boolean) ?? value) : value;
}

/**
 * Parse tripId from a Firestore document path like "trips/{tripId}/shortlist/{id}".
 * @param path - Document path string
 * @returns The tripId segment, or undefined if not found
 */
export function tripIdFromPath(path: string): string | undefined {
  const parts = path.split('/');
  const i = parts.indexOf('trips');
  return i !== -1 && parts[i + 1] ? parts[i + 1] : undefined;
}

/**
 * Validates that a URL is safe for use as an image source.
 * Only allows http:// and https:// protocols to prevent XSS attacks.
 *
 * @param url - The URL to validate
 * @returns True if the URL is a valid http/https URL, false otherwise
 */
export function isValidImageUrl(url: string | undefined): url is string {
  if (!url) return false;

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Format an address object into a readable string
 * @param address - The address object to format (can be undefined or null)
 * @returns Formatted address string, or empty string if address is undefined or null
 */
export function formatAddress(
  address:
    | {
        city?: string;
        country?: string;
        countryCode?: string;
        cityCode?: string;
        line1?: string;
        line2?: string;
        postalCode?: string;
      }
    | null
    | undefined,
  separator = ', ',
): string {
  if (!address) return '';

  const parts: string[] = [];

  if (address.line1) {
    parts.push(address.line1);
  }
  if (address.line2) {
    parts.push(address.line2);
  }
  if (address.city) {
    parts.push(address.city);
  }
  if (address.postalCode) {
    parts.push(address.postalCode);
  }
  if (address.country) {
    parts.push(address.country);
  }

  return parts.join(separator);
}

/** Return hydrated data from a document handle when ready. */
export function hydratedFromHandle<T>(h: { isReady: boolean; data: T | undefined }): T | undefined {
  return h.isReady ? h.data : undefined;
}

/**
 * Returns an icon name for room availability based on the number of available rooms.
 * @param availableRooms - The number of available rooms
 * @returns The icon name
 */
export function roomAvailabilityIcon(availableRooms: number): IconName {
  if (availableRooms === 0) return 'circle';
  if (availableRooms < 3) return 'clock_loader_90';
  if (availableRooms < 5) return 'clock_loader_60';
  return 'clock_loader_10';
}

/**
 * Format a room occupancy as a single human-readable line. Accepts either
 * shape we carry: the booking shape (`{ adults, children: [{ age }] }`,
 * `booking-schemas`) or the rate-search shape (`{ adults, children?, childrenAges? }`,
 * `room-schemas`). Returns an empty string for `undefined` so callers can
 * pass `rate?.occupancy` without a wrapping guard.
 */
export function formatBookingOccupancy(occupancy: BookingOccupancy | RateOccupancy | undefined): string {
  if (!occupancy) return '';
  const adults = `${occupancy.adults} ${occupancy.adults === 1 ? 'adult' : 'adults'}`;
  const { count, ages } = normalizeChildren(occupancy);
  if (count === 0) return adults;
  const childWord = count === 1 ? 'child' : 'children';
  if (ages.length === 0) return `${adults}, ${count} ${childWord}`;
  const agesLabel = ages.map((age) => (age === 0 ? 'under 1' : String(age))).join(', ');
  return `${adults}, ${count} ${childWord} (age ${agesLabel})`;
}

function normalizeChildren(occupancy: BookingOccupancy | RateOccupancy): { count: number; ages: number[] } {
  const children = occupancy.children;
  if (Array.isArray(children)) {
    return { count: children.length, ages: children.map((c) => c.age) };
  }
  const ages = (occupancy as RateOccupancy).childrenAges ?? [];
  return { count: children ?? 0, ages };
}
